import React, { useState, useEffect } from 'react'
import s from './ReviewForm.module.scss'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import type { ProductReviewRatingMetadata } from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import {
    ReviewFormProps,
    ReviewFormInput,
    ReviewInputVariableInterface
} from '@pylot-data/fwrdschema'
import { createProductReviewMutation } from '@pylot-data/api/operations/graphql/createProductReviewMutation'
import { productReviewRatingsMetadata } from '@pylot-data/api/operations/graphql/productReviewsMetadataQuery'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import StarRatings from 'react-star-ratings'
import Button from '@corratech/pylot-ui/src/Button/Button'
import { Success } from '@components/icons'
import { Cross } from '@components/icons'

const ratingSpecs = {
    starDimension: '25px',
    starSpacing: '4px',
    starEmptyColor: 'white',
    starRatedColor: '#696158',
    starHoverColor: '#696158',
    svgIconViewBox: '0 0 27 25'
}

const defaultStarEncodedValue = {
    value_id: 'MTY=',
    value: '1'
}

const RATING_STRING = 'rating'

const svgPath =
    'M21.2036 8.78907C22.8223 8.91661 25.4585 9.21724 25.5972 9.59986C25.921 10.4926 19.0946 14.911 19.0946 14.911C19.0946 14.911 22.0638 22.5817 21.0093 23.5109C20.1398 24.2579 13.5817 19.1016 13.5817 19.1016C13.5817 19.1016 6.57038 23.7477 6.15414 23.5018C4.94241 22.773 8.06885 14.9019 8.06885 14.9019C8.06885 14.9019 -0.607465 10.2922 1.56624 9.59075C4.50767 8.6342 9.81706 8.67975 9.81706 8.67975C9.81706 8.67975 12.1018 1 13.5817 1C14.7472 1 17.3464 8.68886 17.3464 8.68886C17.3464 8.68886 19.2334 8.68886 21.2036 8.78907Z'

const ReviewForm: React.FC<
    ReviewFormProps & {
        /** Evita mostrar el correo de invitado mientras se resuelve la sesión. */
        authLoading?: boolean
    }
> = ({ sku, customer, handleCloseForm, isSignedIn, authLoading = false }) => {
    const { t } = useTranslation(['account'])
    const specs = ratingSpecs
    const { locale } = useRouter()
    const [starRate, setStarRate] = useState(0)
    const [charCount, setCharCount] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [hasSetRating, setHasSetRating] = useState(false)
    const [starErrorMsg, setStarErrorMsg] = useState(false)
    const [successMessage, setSuccessMessage] = useState(false)
    const [ratingMetadata, setRatingMetadata] =
        useState<ProductReviewRatingMetadata | null>(null)

    const {
        register,
        handleSubmit,
        getValues,
        reset,
        formState: { errors }
    } = useForm<ReviewInputVariableInterface>()

    const fetchRatingsMetadata = async () => {
        const response = await graphqlFetch<ReviewFormInput, any>({
            query: productReviewRatingsMetadata
        })

        if (response.data) {
            return response.data.productReviewRatingsMetadata.items[0]
        }
    }

    useEffect(() => {
        fetchRatingsMetadata().then((data) => {
            if (data) {
                setRatingMetadata(data)
            }
        })
    }, [])

    const handleReviewForm = async () => {
        setStarErrorMsg(false)

        if (!hasSetRating) {
            setStarErrorMsg(true)
            return
        }

        setIsLoading(true)
        const formValues = getValues()

        const selectedValue =
            (ratingMetadata &&
                ratingMetadata.values.find(
                    (item) => item?.value === starRate.toString()
                )) ||
            defaultStarEncodedValue

        const ratingIdValue = ratingMetadata ? ratingMetadata.id : 'NA=='
        const name = isSignedIn ? customer?.firstname : formValues.guestEmail
        const response = await graphqlFetch<ReviewFormInput, any>({
            query: createProductReviewMutation,
            variables: {
                input: {
                    sku: sku,
                    nickname: name ? name : '',
                    summary: 'summary',
                    text: formValues.comment,
                    ratings: [
                        {
                            id: ratingIdValue,
                            value_id: selectedValue?.value_id
                        }
                    ]
                }
            },
            locale
        })

        if (response?.data) {
            setSuccessMessage(true)
            setIsLoading(false)
            setHasSetRating(false)
            setStarRate(0)
            reset()
        } else {
            setIsLoading(false)
        }
    }

    const changeRating = (newRating?: number | 0) => {
        if (newRating) {
            setStarRate(newRating)
            setHasSetRating(true)
            setStarErrorMsg(false)
        }
    }

    const handleCommentChange = (e: any) => {
        const inputValue = e.target.value
        setCharCount(inputValue.length)
    }

    const showGuestEmailField = !isSignedIn && !authLoading

    const buttonLabel = isLoading
        ? t('account||account|Review submit loading')
        : t('account||account|Review submit')

    const successMessageSection = successMessage == true && (
        <div className={s['success-container']}>
            <div className={s['success-info']}>
                <Success />
                {t('account||account|Review success message')}
            </div>
            <button onClick={() => setSuccessMessage(false)}>
                <Cross />
            </button>
        </div>
    )

    return (
        <div className={s['form-layout']}>
            <form onSubmit={handleSubmit(handleReviewForm)}>
                <div>
                    <div className={s['form-rating-container']}>
                        <p>{starRate}</p>
                        <StarRatings
                            {...specs}
                            rating={starRate}
                            changeRating={changeRating}
                            svgIconPath={svgPath}
                            name={RATING_STRING}
                        />
                    </div>
                    {starErrorMsg && (
                        <p className={s['form-error-stars']}>
                            {t('account||account|Review select rating error')}
                        </p>
                    )}
                    <div className={s['form-comment']}>
                        {showGuestEmailField && (
                            <div className={s['form-email']}>
                                <label htmlFor="email">
                                    {t(
                                        'account||account|Review guest email label'
                                    )}
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    defaultValue=""
                                    {...register('guestEmail')}
                                    required
                                />
                            </div>
                        )}
                        <label htmlFor="comment">
                            {t('account||account|Review comment label')}
                        </label>
                        {errors?.comment! && (
                            <span className={s['form-error']}>
                                {t('account||account|Review comment required')}
                            </span>
                        )}
                        <textarea
                            defaultValue=""
                            {...register('comment', {
                                required: true
                            })}
                            maxLength={1000}
                            onChange={handleCommentChange}
                            id="comment"
                            placeholder={t(
                                'account||account|Review comment placeholder'
                            )}
                        />
                        <div className={s['form-counter']}>
                            {charCount}{' '}
                            {t(
                                'account||account|Review character counter suffix'
                            )}
                        </div>
                    </div>
                </div>
                {successMessageSection}
                <div className={s['form-actions']}>
                    <Button type="submit">{buttonLabel}</Button>
                    <Button
                        onClick={() => handleCloseForm()}
                        type="button"
                        variant="secondary"
                    >
                        {t('account||account|Cancel')}
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default ReviewForm
